import React, { useCallback, useEffect, useState } from 'react';
import { Box, Button, Chip, CircularProgress, IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import RefreshIcon from '@mui/icons-material/Refresh';
import SyncIcon from '@mui/icons-material/Sync';
import { ModalIndices, TabIndices } from '../../../common/i-doc/i-doc.registry.ts';
import { PageCard } from '@components/common/PageCard.tsx';
import { PageHint } from '@components/common/PageHint.tsx';
import { BaseDialog } from '@components/common/BaseDialog.tsx';
import { EhiSummaryDetailsModal } from './EhiSummaryDetailsModal.tsx';
import { EHI_TREND_COLOR, EhiTrendIndicator, getEhiTrendDirection } from './EhiTrendIndicator.tsx';
import { EhiTrendCell } from './EhiTrendCell.tsx';
import { ConfirmationDialog } from '@components/common/ConfirmationDialog.tsx';
import { PageToolbar } from '@components/common/PageToolbar.tsx';
import { UniversalPagination } from '@components/common/UniversalPagination.tsx';
import { useDebounce } from '@hooks/useDebounce.ts';
import { useEhiOptions } from '@hooks/useEhiOptions.ts';
import { ehiService, EhiLevel, EhiRecalculateSummariesResult, EhiSummaryRow, EhiUserRefreshResult } from '@services/ehi.service.ts';
import { toastService } from '@services/toast.service.ts';

const LEVEL_COLORS: Record<EhiLevel, 'error' | 'warning' | 'info' | 'success'> = {
  low: 'error',
  unstable: 'warning',
  good: 'info',
  strong: 'success',
};

// Таб "Узагальнені": 1 запис на користувача - EHI за 30 днів на основі знімків. Перегляд + комплексне оновлення рядка.
export const EhiSummariesTab: React.FC = () => {
  const { t } = useTranslation();
  const { levels } = useEhiOptions();
  const labelOf = (options: { value: string; label: string }[], value: string) => options.find((o) => o.value === value)?.label ?? value;

  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 400);
  const [ehiLevel, setEhiLevel] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);

  const [items, setItems] = useState<EhiSummaryRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [recalculating, setRecalculating] = useState(false);
  const [detailsUserId, setDetailsUserId] = useState<number | null>(null);
  const [result, setResult] = useState<EhiRecalculateSummariesResult | null>(null);
  // Комплексне оновлення одного користувача (кнопка в рядку, 2026-09-25): підтвердження -> запит -> підсумок.
  const [refreshTarget, setRefreshTarget] = useState<EhiSummaryRow | null>(null);
  const [refreshingUserId, setRefreshingUserId] = useState<number | null>(null);
  const [userRefreshResult, setUserRefreshResult] = useState<EhiUserRefreshResult | null>(null);

  const fetchSummaries = useCallback(async () => {
    setLoading(true);
    try {
      const res = await ehiService.getSummaries({
        page,
        limit,
        search: debouncedSearch.trim() || undefined,
        ehiLevel: (ehiLevel as EhiLevel) || undefined,
      });
      setItems(res.items);
      setTotal(res.total);
    } catch {
      toastService.error(t('ehiJournal.loadError'));
    } finally {
      setLoading(false);
    }
  }, [page, limit, debouncedSearch, ehiLevel, t]);

  const handleRecalculate = async () => {
    setConfirmOpen(false);
    setRecalculating(true);
    try {
      setResult(await ehiService.recalculateSummaries());
      await fetchSummaries();
    } catch {
      toastService.error(t('ehiJournal.recalcError'));
    } finally {
      setRecalculating(false);
    }
  };

  const handleUserRefresh = async () => {
    const target = refreshTarget;
    setRefreshTarget(null);
    if (!target) return;
    setRefreshingUserId(target.userId);
    try {
      setUserRefreshResult(await ehiService.refreshUser(target.userId));
      await fetchSummaries();
    } catch {
      toastService.error(t('ehiJournal.userRefreshError'));
    } finally {
      setRefreshingUserId(null);
    }
  };

  useEffect(() => {
    fetchSummaries();
  }, [fetchSummaries]);

  return (
    <Box data-i-doc={TabIndices.ADMIN_EHI_SUMMARIES}>
      <PageHint iDoc={TabIndices.ADMIN_EHI_SUMMARIES} />

      <PageCard>
        <PageToolbar
          storageKey="filters:admin-ehi-summaries"
          filters={[
            {
              id: 'search',
              type: 'text',
              label: t('ehiJournal.searchUser'),
              value: search,
              onChange: (v) => { setSearch(v); setPage(1); },
              flexGrow: 1,
            },
            {
              id: 'ehiLevel',
              type: 'select',
              label: t('ehiJournal.colLevel'),
              value: ehiLevel,
              onChange: (v) => { setEhiLevel(v); setPage(1); },
              options: [{ value: '', label: t('ehiJournal.all') }, ...levels],
            },
          ]}
          onReset={() => { setSearch(''); setEhiLevel(''); setPage(1); }}
          showReset
          extraActions={
            <Button
              variant="outlined"
              startIcon={recalculating ? <CircularProgress size={16} /> : <RefreshIcon />}
              disabled={recalculating}
              onClick={() => setConfirmOpen(true)}
            >
              {t('ehiJournal.recalcButton')}
            </Button>
          }
        />

        {loading ? (
          <Box display="flex" justifyContent="center" sx={{ mt: 4 }}>
            <CircularProgress />
          </Box>
        ) : items.length === 0 ? (
          <Typography sx={{ mt: 4, textAlign: 'center' }}>{t('ehiJournal.summariesEmpty')}</Typography>
        ) : (
          <TableContainer>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('ehiJournal.colUser')}</TableCell>
                  <TableCell align="right">{t('ehiJournal.colEhi')}</TableCell>
                  <TableCell>{t('ehiJournal.colTrend')}</TableCell>
                  <TableCell>{t('ehiJournal.colLevel')}</TableCell>
                  <TableCell align="right">{t('ehiJournal.colDays')}</TableCell>
                  <TableCell>{t('ehiJournal.colUpdated')}</TableCell>
                  <TableCell align="right">{t('ehiJournal.colActions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((row) => {
                  const trend = getEhiTrendDirection(row.windowSnapshots);
                  return (
                  <TableRow key={row.userId} hover sx={{ cursor: 'pointer' }} onClick={() => setDetailsUserId(row.userId)}>
                    <TableCell>
                      <Typography variant="body2">{row.userName ?? row.userEmail ?? '—'}</Typography>
                      {row.userName && <Typography variant="caption" color="text.secondary">{row.userEmail}</Typography>}
                    </TableCell>
                    <TableCell align="right">
                      {/* Індикатор руху - зі знімків вікна, збережених у самому записі (оновлюється разом з EHI в цьому рядку). */}
                      <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                        <EhiTrendIndicator snapshots={row.windowSnapshots} />
                        {/* Колір числа - за напрямком стрілки (зростання - позитивне), без руху - звичайний. */}
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          color={trend ? EHI_TREND_COLOR[trend] : 'text.primary'}
                        >
                          {row.ehiValueDisplay}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {/* Графік денних знімків за вікно - з самого запису (оновлюється разом з EHI в цьому рядку). */}
                      <EhiTrendCell snapshots={row.windowSnapshots} />
                    </TableCell>
                    <TableCell>
                      <Chip size="small" variant="outlined" color={LEVEL_COLORS[row.ehiLevel]} label={labelOf(levels, row.ehiLevel)} />
                    </TableCell>
                    <TableCell align="right">{row.closedDaysCount}/30</TableCell>
                    <TableCell>{new Date(row.updatedAt).toLocaleString()}</TableCell>
                    <TableCell align="right">
                      <Tooltip title={t('ehiJournal.userRefreshTooltip')}>
                        <span>
                          <IconButton
                            size="small"
                            disabled={refreshingUserId !== null}
                            // Клік по кнопці не відкриває деталі рядка (onClick на TableRow).
                            onClick={(e) => { e.stopPropagation(); setRefreshTarget(row); }}
                          >
                            {refreshingUserId === row.userId ? <CircularProgress size={18} /> : <SyncIcon fontSize="small" />}
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <UniversalPagination
          page={page}
          count={Math.ceil(total / limit)}
          limit={limit}
          total={total}
          onPageChange={setPage}
          onLimitChange={(v) => { setLimit(v); setPage(1); }}
          storageKey="pagination:admin-ehi-summaries"
        />
      </PageCard>

      <ConfirmationDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleRecalculate}
        title={t('ehiJournal.recalcTitle')}
        message={t('ehiJournal.recalcMessage')}
        confirmText={t('ehiJournal.recalcConfirm')}
        cancelText={t('ehiJournal.refreshCancel')}
      />

      <ConfirmationDialog
        open={refreshTarget !== null}
        onClose={() => setRefreshTarget(null)}
        onConfirm={handleUserRefresh}
        title={t('ehiJournal.userRefreshTitle')}
        message={t('ehiJournal.userRefreshMessage', { user: refreshTarget?.userName ?? refreshTarget?.userEmail ?? `#${refreshTarget?.userId}` })}
        confirmText={t('ehiJournal.userRefreshConfirm')}
        cancelText={t('ehiJournal.refreshCancel')}
      />

      <EhiSummaryDetailsModal userId={detailsUserId} onClose={() => setDetailsUserId(null)} />

      <BaseDialog
        open={userRefreshResult !== null}
        onClose={() => setUserRefreshResult(null)}
        title={t('ehiJournal.userRefreshResultTitle')}
        iDoc={ModalIndices.ADMIN_EHI_USER_REFRESH_RESULT}
        maxWidth="xs"
        actions={<Button onClick={() => setUserRefreshResult(null)}>{t('ehiJournal.close')}</Button>}
      >
        {userRefreshResult && (
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr auto', rowGap: 0.75, columnGap: 2 }}>
            {[
              [
                t('ehiJournal.userRefreshRange'),
                userRefreshResult.range
                  ? `${userRefreshResult.range.dateFrom} – ${userRefreshResult.range.dateTo}`
                  : t('ehiJournal.userRefreshNoRange'),
              ],
              ...(userRefreshResult.startSnapshotDate ? [[t('ehiJournal.userRefreshStart'), userRefreshResult.startSnapshotDate]] : []),
              [t('ehiJournal.resultCollected'), userRefreshResult.collected],
              [t('ehiJournal.resultSkipped'), userRefreshResult.skipped],
              [t('ehiJournal.resultFailed'), userRefreshResult.failed],
              [t('ehiJournal.userRefreshEhi'), `${userRefreshResult.ehiBefore ?? '—'} → ${userRefreshResult.ehiAfter ?? '—'}`],
            ].map(([label, value]) => (
              <React.Fragment key={String(label)}>
                <Typography variant="body2" color="text.secondary">{label}</Typography>
                <Typography variant="body2" fontWeight={600} textAlign="right">{value}</Typography>
              </React.Fragment>
            ))}
            {userRefreshResult.failed > 0 && (
              <Typography variant="caption" color="error" sx={{ gridColumn: '1 / -1', mt: 1 }}>
                {userRefreshResult.days.filter((d) => d.result === 'failed').map((d) => d.date).join(', ')}
              </Typography>
            )}
          </Box>
        )}
      </BaseDialog>

      <BaseDialog
        open={result !== null}
        onClose={() => setResult(null)}
        title={t('ehiJournal.recalcResultTitle')}
        iDoc={ModalIndices.ADMIN_EHI_SUMMARY_RECALC_RESULT}
        maxWidth="xs"
        actions={<Button onClick={() => setResult(null)}>{t('ehiJournal.close')}</Button>}
      >
        {result && (
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr auto', rowGap: 0.75, columnGap: 2 }}>
            {[
              [t('ehiJournal.resultTotal'), result.totalUsers],
              [t('ehiJournal.resultCollected'), result.updated],
              [t('ehiJournal.recalcNoSnapshots'), result.skippedNoSnapshots],
              [t('ehiJournal.resultFailed'), result.failed],
              [t('ehiJournal.resultDuration'), `${result.durationMs} ${t('ehiJournal.ms')}`],
            ].map(([label, value]) => (
              <React.Fragment key={String(label)}>
                <Typography variant="body2" color="text.secondary">{label}</Typography>
                <Typography variant="body2" fontWeight={600} textAlign="right">{value}</Typography>
              </React.Fragment>
            ))}
            {result.failedUserIds.length > 0 && (
              <Typography variant="caption" color="error" sx={{ gridColumn: '1 / -1', mt: 1 }}>
                {t('ehiJournal.resultFailedIds', { ids: result.failedUserIds.join(', ') })}
              </Typography>
            )}
          </Box>
        )}
      </BaseDialog>
    </Box>
  );
};
